import { useNavigate, useSearchParams } from "react-router-dom";

import { useT } from "@repo/transifex";
import { useCurrentTill } from "~/api/tills/hooks/use-till-current";
import { toast } from "~/components/hooks/use-toast";
import { basketActions } from "~/providers/store/basket";
import { collectActions } from "~/providers/store/collect/actions";
import { useCollectFlow } from "~/providers/store/collect/hooks";
import {
  employeeActions,
  useEmployeeIdentifier
} from "~/providers/store/employee";
import { hasAttendedSellConfig } from "~/utils/tills/has-attended-sell-config";
import { useInvalidateProducts } from "~/api/products/hooks/use-invalidate-products";
import { ToastAction } from "~/components/ui/toast";
import { useAppContext, usePrinterStatus } from "~/providers/app";

import { usePrintOrderReceipt } from "./queries/orders";

/**
 * @description This hook is used to skip the receipt page and show the payment success right panel.
 * @returns A function that takes an orderId and set it as a search param to show the payment success right panel.
 */
export function useSkipReceipt() {
  const navigate = useNavigate();
  const t = useT();

  const [, setSearchParams] = useSearchParams();

  const { data: till } = useCurrentTill();

  const {
    hardware: { printer }
  } = useAppContext();

  const employeeIdentifier = useEmployeeIdentifier();

  const { mutate: createReceipt, status } = usePrintOrderReceipt();

  const printerStatus = usePrinterStatus();

  const collectFlow = useCollectFlow();

  const invalidateProducts = useInvalidateProducts();

  return (orderId: string) => {
    if (
      (!hasAttendedSellConfig(till) && till?.type !== "self-service") ||
      !till.config.skipReceiptPage
    ) {
      return;
    }

    basketActions.reset();
    employeeActions.reset();

    invalidateProducts(employeeIdentifier?.value);

    if (
      till?.type === "attended" &&
      till.preCheckoutTextInputs?.length &&
      till.config.resetDiningOption
    ) {
      basketActions.resetDiningOption();
      navigate("/pos");
      return;
    }

    if (till.type === "self-service") {
      if (
        till.config.selectorSetup === "show-selectors" ||
        till.preCheckoutTextInputs?.length ||
        till.config.hasEmployeeCardReader
      ) {
        if (till.preCheckoutTextInputs?.length) {
          basketActions.resetDiningOption();
        }
        toast({
          title: t("Payment completed"),
          variant: "success",
          position: "top",
          toastDuration: "long",
          action:
            printer &&
            (till.config.receiptPrintMode === "manual" ||
              till.config.receiptPrintMode === "automatic") ? (
              <ToastAction
                altText={t("Print receipt")}
                disabled={status === "pending" || printerStatus === "loading"}
                onClick={() => {
                  createReceipt({ orderId, userId: null });
                }}
              >
                {t("Print receipt")}
              </ToastAction>
            ) : undefined
        });
      }
    }

    // if the user arrives from collect, but didn't collect any products
    // they should be redirected to the collect page
    if (collectFlow === "collect-pos" || collectFlow === "collect") {
      toast({
        title: t("Purchase completed successfully"),
        variant: "success"
      });
      navigate("/collect");
      collectActions.reset();
      return;
    }

    navigate("/pos/sell");
    setSearchParams({ orderId, skipReceipt: "true" });
  };
}
